import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowRight, FaCheckCircle, FaLeaf, FaIndustry, FaTruck, FaShieldAlt,
  FaAppleAlt, FaSeedling, FaHardHat, FaFlask, FaTshirt, FaCog
} from 'react-icons/fa';

const ICON_MAP = { FaAppleAlt, FaSeedling, FaHardHat, FaTruck, FaTshirt, FaFlask, FaCog, FaIndustry };

const StatCard = ({ value, label }) => (
  <div className="text-center px-6 py-8">
    <div className="text-4xl font-extrabold text-secondary mb-1">{value}</div>
    <div className="text-gray-600 text-sm font-medium">{label}</div>
  </div>
);

const ProductCard = ({ product }) => (
  <Link to={`/urunler/${product.slug}`} className="card group cursor-pointer">
    <div className="h-52 img-placeholder overflow-hidden">
      {product.main_image ? (
        <img src={product.main_image} alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
          <FaIndustry size={48} className="mb-2 opacity-30" />
        </div>
      )}
    </div>
    <div className="p-5">
      {product.category_name && (
        <span className="badge bg-secondary/10 text-secondary mb-2 inline-block">{product.category_name}</span>
      )}
      <h3 className="font-semibold text-primary text-lg mb-2 group-hover:text-secondary transition-colors">{product.name}</h3>
      <p className="text-gray-500 text-sm line-clamp-2 mb-4">{product.short_description}</p>
      <span className="text-secondary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
        Detayları Gör <FaArrowRight size={12} />
      </span>
    </div>
  </Link>
);

const ServiceCard = ({ service }) => {
  const Icon = ICON_MAP[service.icon] || FaCog;
  return (
    <Link to={`/hizmetler/${service.slug}`} className="card p-6 group cursor-pointer hover:-translate-y-1">
      <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-secondary transition-colors duration-300">
        <Icon className="text-secondary group-hover:text-white text-2xl transition-colors duration-300" />
      </div>
      <h3 className="font-semibold text-primary text-lg mb-2">{service.name}</h3>
      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{service.short_description}</p>
      <div className="mt-4 text-secondary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
        İncele <FaArrowRight size={12} />
      </div>
    </Link>
  );
};

export default function Home() {
  const [settings, setSettings] = useState({});
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get('/api/settings').then(r => setSettings(r.data)).catch(() => {});
    axios.get('/api/products?featured=true').then(r => setProducts(r.data.slice(0, 6))).catch(() => {});
    axios.get('/api/services').then(r => setServices(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const heroTitle = settings.hero_title || 'Kaliteli Ürünler, Güvenilir Çözümler';
  const commaIdx = heroTitle.indexOf(',');
  const titleLine1 = commaIdx > -1 ? heroTitle.slice(0, commaIdx) : heroTitle;
  const titleLine2 = commaIdx > -1 ? heroTitle.slice(commaIdx + 1).trim() : '';

  return (
    <div>
      {/* HERO */}
      <section className="relative hero-gradient hero-pattern min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full relative z-10">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-4 py-1.5 rounded-full mb-6 border border-white/20 backdrop-blur-sm">
              <FaCheckCircle size={12} className="text-secondary" /> Türkiye'nin Güvenilir Ambalaj Çözümleri
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
              <span className="text-white">{titleLine1}</span>
              {titleLine2 && (
                <>
                  <span className="text-white">,</span>
                  <br />
                  <span className="text-secondary">{titleLine2}</span>
                </>
              )}
            </h1>
            <p className="text-gray-200 text-lg sm:text-xl mb-10 leading-relaxed max-w-2xl">
              {settings.hero_subtitle || 'Ambalaj sektöründe 20 yılı aşkın deneyimimizle ihtiyaçlarınıza en uygun çözümleri sunuyoruz.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/urunler" className="btn-primary text-base px-8 py-4">
                Ürünleri Keşfet <FaArrowRight />
              </Link>
              <Link to="/iletisim" className="btn-outline text-base px-8 py-4">
                Teklif Alın
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-6">
              {['ISO 9001:2015 Sertifikalı', '20+ Yıl Deneyim', '1000+ Mutlu Müşteri'].map(f => (
                <div key={f} className="flex items-center gap-2 text-gray-200 text-sm font-medium">
                  <FaCheckCircle className="text-secondary flex-shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute right-0 top-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute right-32 bottom-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
            <StatCard value={settings.stat_experience || '20+'} label="Yıl Deneyim" />
            <StatCard value={settings.stat_products || '500+'} label="Farklı Ürün" />
            <StatCard value={settings.stat_customers || '1000+'} label="Mutlu Müşteri" />
            <StatCard value={settings.stat_exports || '50+'} label="Ülkeye İhracat" />
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">Ürünlerimiz</span>
            <h2 className="section-title mt-2">Öne Çıkan Ürünler</h2>
            <p className="section-subtitle">Sektörün ihtiyaçlarına yönelik geliştirdiğimiz yüksek kaliteli ürün yelpazemizi keşfedin.</p>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <FaIndustry size={48} className="mx-auto mb-4 opacity-20" />
              <p>Henüz ürün eklenmemiş.</p>
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/urunler" className="btn-secondary">
              Tüm Ürünleri Gör <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">Hizmetlerimiz</span>
            <h2 className="section-title mt-2">Sektörlere Özel Çözümler</h2>
            <p className="section-subtitle">Farklı sektörlerin ihtiyaçlarına göre özelleştirilmiş ambalaj ve çözüm hizmetleri sunuyoruz.</p>
          </div>

          {services.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map(s => <ServiceCard key={s.id} service={s} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <FaCog size={48} className="mx-auto mb-4 opacity-20" />
              <p>Henüz hizmet eklenmemiş.</p>
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/hizmetler" className="btn-secondary">
              Tüm Hizmetleri Gör <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-secondary font-semibold text-sm uppercase tracking-wider">Neden Biz?</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
                Sektörde Fark Yaratan Değerlerimiz
              </h2>
              <p className="text-gray-300 leading-relaxed mb-8">
                {settings.about_text || '20 yılı aşkın deneyimimizle müşterilerimize en kaliteli ürün ve hizmeti sunmak için çalışıyoruz.'}
              </p>
              <Link to="/hakkimizda" className="btn-primary">
                Hakkımızda Daha Fazla <FaArrowRight />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: FaShieldAlt, title: 'Kalite Güvencesi', desc: 'ISO 9001:2015 sertifikalı üretim süreçleri' },
                { icon: FaLeaf, title: 'Sürdürülebilirlik', desc: 'Çevre dostu ve geri dönüştürülebilir malzemeler' },
                { icon: FaTruck, title: 'Hızlı Teslimat', desc: 'Türkiye genelinde hızlı ve güvenli kargo' },
                { icon: FaIndustry, title: 'Özel Üretim', desc: 'İhtiyaca özel tasarım ve üretim kapasitesi' },
              ].map(item => (
                <div key={item.title} className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <item.icon className="text-secondary text-2xl mb-3" />
                  <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                  <p className="text-gray-300 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Bizimle Çalışmaya Başlayın</h2>
          <p className="text-orange-100 mb-8">Ürünlerimiz ve hizmetlerimiz hakkında detaylı bilgi almak için hemen iletişime geçin.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/iletisim" className="btn-outline">
              İletişime Geçin <FaArrowRight />
            </Link>
            <Link to="/urunler" className="bg-white text-secondary hover:bg-gray-50 font-semibold px-6 py-3 rounded-lg transition-all inline-flex items-center gap-2">
              Ürünleri İncele
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
